import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import BundleBanner from "./BundleBanner";
import ExtraServices from "components/ExtraServices";

const useStyles = makeStyles((theme) => ({
  progress: {
    margin: theme.spacing(2),
  },
}));

const Bundles = (props) => {
  const {
    bundles,
    selectedBundles,
    fetchingBundles,
    onSelectBundle,
    openedBundle,
    onOpenBundle,

    fetchingExtraServices,
    extraServices,
    selectedExtraServices,
    onSelectExtraService,
    onDeselectOptionalExtraServices,
  } = props;

  const classes = useStyles();

  const extraServicesWithSelection = useMemo(() => {
    const selectedBundle = bundles
      .flatMap((b) => b.bundles)
      .find((b) => selectedBundles.includes(b.id));

    if (!selectedBundle) {
      return extraServices.map((s) => ({
        ...s,
        isSelected: false,
      }));
    }

    const allItems = selectedBundle.packageItems.map((b) => b.alternativeLabel);

    return extraServices.map((s) => ({
      ...s,
      isSelected: allItems.includes(s.alternative),
    }));
  }, [extraServices, bundles, selectedBundles]);

  const { requiredExtraServices, optionalExtraServices } = useMemo(() => {
    return {
      requiredExtraServices: extraServicesWithSelection.filter(
        (x) => !x.optional,
      ),
      optionalExtraServices: extraServicesWithSelection.filter(
        (x) => x.optional,
      ),
    };
  }, [extraServicesWithSelection]);

  const activeBundle = useMemo(() => {
    for (const group of bundles) {
      const selected = group.bundles.find((b) =>
        selectedBundles.includes(b.id),
      );
      if (selected) {
        return { group, selected };
      }
    }
    return undefined;
  }, [bundles, selectedBundles]);

  const { firstTwoBundles, othersBundles } = useMemo(
    () => ({
      firstTwoBundles: bundles.slice(0, 2),
      othersBundles: bundles.slice(2),
    }),
    [bundles],
  );

  if (fetchingBundles || fetchingExtraServices) {
    return (
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <Typography variant="h5" className="v-divider">
            Caricamento in corso
          </Typography>
          <CircularProgress className={classes.progress} />
        </Box>
      </Grid>
    );
  }

  if (extraServices.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="h5" className="v-divider">
          Nessun servizio extra per questo pacchetto è disponibile
        </Typography>
      </Grid>
    );
  }

  if (bundles.length === 0) {
    return (
      <Grid item xs={12}>
        <ExtraServices
          allExtraServices={extraServices}
          selectedExtraServices={selectedExtraServices}
          onSelectExtraService={onSelectExtraService}
          onDeselectOptionalExtraServices={onDeselectOptionalExtraServices}
        />
      </Grid>
    );
  }

  return (
    <>
      {/* First bundle banner + required extra services +  second bundle banner */}
      <Grid item xs={12}>
        {firstTwoBundles.map((el, i) => {
          const selectedBundle = el.bundles.find((b) =>
            selectedBundles.includes(b.id),
          );

          return (
            <React.Fragment key={el.code}>
              <BundleBanner
                bundle={el}
                onClick={() => {
                  onOpenBundle(el.id);
                  document.body.style.overflow = "hidden";
                }}
                isOpened={openedBundle === el.id}
                onCloseDialog={() => {
                  onOpenBundle(undefined);
                  document.body.style.overflow = "auto";
                }}
                onSelectBundlePack={(bundleId) => {
                  onSelectBundle(
                    bundleId,
                    selectedBundle ? selectedBundle.id : undefined,
                  );
                  onOpenBundle(undefined);
                  document.body.style.overflow = "auto";
                }}
                selectedBundle={selectedBundle}
              />

              {i === 0 && (
                <ExtraServices
                  allExtraServices={requiredExtraServices}
                  selectedExtraServices={selectedExtraServices}
                  onSelectExtraService={onSelectExtraService}
                  onDeselectOptionalExtraServices={
                    onDeselectOptionalExtraServices
                  }
                  onOpenBundle={() => onOpenBundle(el.id)}
                  bundleTitle={el.title}
                  bundleColorStyle={el.colorStyle}
                  onRemoveBundle={() => onSelectBundle(selectedBundle.id)}
                />
              )}
            </React.Fragment>
          );
        })}
      </Grid>

      {/* Optional extra services */}
      <Grid item xs={12}>
        <ExtraServices
          allExtraServices={optionalExtraServices}
          selectedExtraServices={selectedExtraServices}
          onSelectExtraService={onSelectExtraService}
          onDeselectOptionalExtraServices={onDeselectOptionalExtraServices}
          bundleTitle={activeBundle ? activeBundle.group.title : undefined}
          bundleColorStyle={
            activeBundle ? activeBundle.group.colorStyle : undefined
          }
          onRemoveBundle={
            activeBundle
              ? () => onSelectBundle(activeBundle.selected.id)
              : undefined
          }
        />
      </Grid>

      {/* Others bundles */}
      <Grid item xs={12}>
        {othersBundles.map((el) => {
          const selectedBundle = el.bundles.find((b) =>
            selectedBundles.includes(b.id),
          );

          return (
            <React.Fragment key={el.code}>
              <BundleBanner
                bundle={el}
                onClick={() => {
                  onOpenBundle(el.id);
                  document.body.style.overflow = "hidden";
                }}
                isOpened={openedBundle === el.id}
                onCloseDialog={() => {
                  onOpenBundle(undefined);
                  document.body.style.overflow = "auto";
                }}
                onSelectBundlePack={(bundleId) => {
                  onSelectBundle(
                    bundleId,
                    selectedBundle ? selectedBundle.id : undefined,
                  );
                  onOpenBundle(undefined);
                  document.body.style.overflow = "auto";
                }}
                selectedBundle={selectedBundle}
              />
            </React.Fragment>
          );
        })}
      </Grid>
    </>
  );
};

export default Bundles;
